import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSeason } from '../../context/SeasonContext';
import { SeasonNavLink as NavLink } from '../Seasons/SeasonLink';
import styles from './sidenav.module.css';
import logo from '../../assets/icons/logo.svg';
import { ReactComponent as SeasonIcon } from '../../assets/icons/sidenav/season.svg';
import { ReactComponent as TeamIcon } from '../../assets/icons/sidenav/team.svg';
import { ReactComponent as RatingIcon } from '../../assets/icons/sidenav/trophy.svg';
import { ReactComponent as AchieveIcon } from '../../assets/icons/sidenav/achieve.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/sidenav/calendar.svg';
import { ReactComponent as KnowledgeIcon } from '../../assets/icons/sidenav/knowledge.svg';
import { ReactComponent as VacanciesIcon } from '../../assets/icons/sidenav/vacancies.svg';
import { ReactComponent as TransactionsIcon } from '../../assets/icons/sidenav/transactions.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/sidenav/settings.svg';
import { ReactComponent as AdminIcon } from '../../assets/icons/sidenav/admin.svg';

const primaryItems = [
  { to: '/my-season', label: 'Мой сезон', short: 'Мой сезон', icon: SeasonIcon },
  { to: '/team', label: 'Мои команды', short: 'Команды', icon: TeamIcon },
  { to: '/schedule', label: 'Расписание', short: 'Расписание', icon: CalendarIcon },
  { to: '/rating', label: 'Рейтинг', short: 'Рейтинг', icon: RatingIcon },
];
const secondaryItems = [
  { to: '/achieve', label: 'Достижения', icon: AchieveIcon },
  { to: '/knowledge', label: 'База знаний', icon: KnowledgeIcon },
  { to: '/vacancies', label: 'Вакансии', icon: VacanciesIcon },
  { to: '/transactions', label: 'Транзакции', icon: TransactionsIcon },
  { to: '/settings', label: 'Настройки', icon: SettingsIcon },
];

function ProfileIcon(props) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <circle cx="12" cy="8" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" />
  </svg>;
}

function HomeIcon(props) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" {...props}>
    <path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z" />
  </svg>;
}

function NavigationLink({ item, inMenu = false, onClick }) {
  const Icon = item.icon;
  return <NavLink
    to={item.to}
    end={item.to === '/'}
    aria-label={item.label}
    title={item.label}
    onClick={onClick}
    className={({ isActive }) => [
      inMenu ? styles.menuLink : styles.sidenavLink,
      isActive ? styles.linkActive : '',
    ].join(' ')}
  >
    <Icon className={styles.icon} aria-hidden="true" focusable="false" />
    <span className={inMenu ? styles.menuLabel : styles.linkLabel}>{inMenu ? item.label : item.short}</span>
  </NavLink>;
}

function MoreNavigation({ id, items, seasonTitle, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = (event) => { if (event.matches) onClose(); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      desktop.removeEventListener('change', closeOnDesktop);
      dialog.close();
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [onClose]);

  return createPortal(
    <dialog
      ref={ref}
      id={id}
      className={styles.moreDialog}
      aria-labelledby={`${id}-title`}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={styles.sheetContent}>
        <div className={styles.sheetHeader}>
          <div>
            <h2 id={`${id}-title`}>Разделы платформы</h2>
            <p>{seasonTitle}</p>
          </div>
          <button type="button" className={styles.closeButton} aria-label="Закрыть меню" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
          </button>
        </div>
        <nav aria-label="Дополнительные разделы" className={styles.menuGrid}>
          {items.map(item => <NavigationLink key={item.to} item={item} inMenu onClick={onClose} />)}
        </nav>
      </div>
    </dialog>,
    document.body,
  );
}

export const SideNav = ({ adminAccess }) => {
  const { season } = useSeason();
  const location = useLocation();
  const [openAt, setOpenAt] = useState(null);
  const menuId = useId();
  // A location change, including a season switch or Back, closes the menu.
  const menuOpen = openAt === location.key;
  const closeMenu = useCallback(() => setOpenAt(null), []);
  useEffect(() => setOpenAt(null), [location.key]);
  const extras = [
    ...secondaryItems,
    ...(adminAccess.allowed ? [{
      to: adminAccess.auctionOnly ? '/admin?section=auction' : '/admin',
      label: 'Админ-панель', icon: AdminIcon,
    }] : []),
  ];
  const menuItems = [
    { to: '/profile', label: 'Мой профиль', icon: ProfileIcon },
    ...extras,
    { to: '/', label: 'На главную', icon: HomeIcon },
  ];
  const moreActive = menuItems.some(({ to }) => {
    const path = to.split('?')[0];
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
  });

  return <>
    <nav className={styles.sidenav} aria-label="Основная навигация">
      <NavLink to="/" end className={styles.logoLink} aria-label="ЮФУ.Про — на главную">
        <img src={logo} alt="" className={styles.logo} />
      </NavLink>
      <ul className={styles.sidenavList}>
        {primaryItems.map(item => <li key={item.to} className={styles.sidenavItem}><NavigationLink item={item} /></li>)}
        {extras.map(item => <li key={item.to} className={`${styles.sidenavItem} ${styles.desktopOnly}`}><NavigationLink item={item} /></li>)}
        <li className={`${styles.sidenavItem} ${styles.mobileOnly}`}>
          <button
            type="button"
            className={`${styles.sidenavLink} ${moreActive || menuOpen ? styles.linkActive : ''}`}
            aria-label="Ещё разделы"
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            onClick={() => setOpenAt(location.key)}
          >
            <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
            <span className={styles.linkLabel}>Ещё</span>
          </button>
        </li>
      </ul>
    </nav>
    {menuOpen && <MoreNavigation id={menuId} items={menuItems} seasonTitle={season?.title} onClose={closeMenu} />}
  </>;
};
